import { useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import {
  IconTool,
  IconHexagon3d,
  IconAdjustmentsHorizontal,
  IconCreditCard,
  IconChevronRight,
  IconApiApp,
  IconWebhook,
  IconBellCog
} from '@tabler/icons-react';
import { IconMenuItem, NestedMenuItem } from 'mui-nested-menu';

import userTypes from 'constants/userTypes';

const SettingsSection = () => {
  const navigate = useNavigate();
  // @ts-expect-error store is not set up for TypeScript
  const userData = useSelector((state) => state.user?.data);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleClose();
  };

  if (userData?.type !== userTypes.Admin) {
    return null;
  }

  return (
    <Box>
      <Box sx={{ cursor: 'pointer', display: 'flex' }} onClick={handleClick}>
        <IconAdjustmentsHorizontal color="#1c1463" size={28} stroke={1.5} />
      </Box>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableAutoFocusItem
        sx={{
          marginTop: 3,
          '& .MuiPaper-root': {
            borderRadius: '12px',
            paddingInline: '10px', // eyeballed it as it looks cramped without
            boxShadow: '0px 8px 10px -5px rgba(0,0,0,0.2),0px 16px 24px 2px rgba(0,0,0,0.14),0px 6px 30px 5px rgba(0,0,0,0.12)' // copied from other popper components that are spawned by header bar
          }
        }}
      >
        <IconMenuItem
          onClick={() => {
            handleNavigation('/manage-app-configurations');
          }}
          leftIcon={<IconTool />}
          label="App Configurations"
        />
        <IconMenuItem
          onClick={() => {
            handleNavigation('/notification-settings');
          }}
          leftIcon={<IconBellCog />}
          label="Notification Settings"
        />
        <IconMenuItem
          onClick={() => {
            handleNavigation('/manage-integrations');
          }}
          leftIcon={<IconHexagon3d />}
          label="App Integrations"
        />
        <NestedMenuItem
          label="Manage API"
          parentMenuOpen={open}
          leftIcon={<IconApiApp />}
          rightIcon={<IconChevronRight />}
          MenuProps={{
            anchorOrigin: { vertical: 'top', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'right' },
            slotProps: {
              paper: {
                sx: {
                  borderRadius: '12px !important',
                  boxShadow:
                    '0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12) !important'
                }
              },
              list: { sx: { padding: '10px' } }
            }
          }}
        >
          <MenuItem onClick={() => handleNavigation('/manage-api')}>
            <IconApiApp size={18} style={{ marginRight: 8 }} />
            API Clients
          </MenuItem>
          <MenuItem onClick={() => handleNavigation('/manage-webhooks')}>
            <IconWebhook size={18} style={{ marginRight: 8 }} />
            Webhooks
          </MenuItem>
        </NestedMenuItem>
        <NestedMenuItem
          label="Billing"
          parentMenuOpen={open}
          leftIcon={<IconCreditCard />}
          rightIcon={<IconChevronRight />}
          MenuProps={{
            anchorOrigin: { vertical: 'top', horizontal: 'left' },
            transformOrigin: { vertical: 'top', horizontal: 'right' },
            slotProps: {
              paper: {
                sx: {
                  borderRadius: '12px !important',
                  boxShadow:
                    '0px 8px 10px -5px rgba(0,0,0,0.2), 0px 16px 24px 2px rgba(0,0,0,0.14), 0px 6px 30px 5px rgba(0,0,0,0.12) !important'
                }
              },
              list: { sx: { padding: '10px' } }
            }
          }}
        >
          <MenuItem
            onClick={() => {
              handleNavigation('/sales-account');
            }}
          >
            Stripe Account
          </MenuItem>
          <MenuItem
            onClick={() => {
              handleNavigation('/manage-billing');
            }}
          >
            SMS Billing
          </MenuItem>
        </NestedMenuItem>
      </Menu>
    </Box>
  );
};

export default SettingsSection;
